import {action, computed, observable, observe} from 'mobx';

import {basemapMaxZoom, generateResponseID} from '../utils/Utils';
import ElementStore from './ElementStore';
import L from "leaflet";
import {formatElementsforExport, postData} from "../utils/Utils";
import TrackingStore from './TrackingStore';

export default class AppStore {
    constructor() {
        this.elementStore = new ElementStore(this);
        this.tracking = new TrackingStore('');

        // Auto close drawer on IconButton press when using mobile
        observe(this.pendingState, (change) => {
            if (change.newValue !== null && this.isMobile) {
                this.drawer.open = false;
            }
        });
        let params = (new URL(document.location)).searchParams;
        let responseId = params.get('response_id');
        if (responseId) {
            this.responseID = responseId;
        } else {
            this.responseID = generateResponseID();
        }
    }

    @action loadConfig(json) {
        this.appConfig = this.applyDefaults(json.config);
        this.environment = json.environment;
        this.branchDisplayName = json.branchDisplayName;
        this.branchId = json.branchId;
        this.projectId = json.projectid;//this need to be changed to camelcase
        this.meta = json.meta;

        const icons = Object.values(this.appConfig.questions).map(type => (
            type.map(question => question.icons).reduce((a, b) => [...a, ...b], [])
        ));
        this.maxIconsLength = Math.max(...icons.map(group => group.length));

        // Open drawer to first question type with icons
        if (!this.drawer.active) {
            this.setDrawer({active: Object.keys(this.appConfig.questions).filter((type) => this.appConfig.questions[type].length > 0)[0]});
        }

        if (Object.keys(this.appConfig.map.baseLayers).length > 1) {
            this.mapState.mode = Object.keys(this.appConfig.map.baseLayers).filter((layer) => this.appConfig.map.baseLayers[layer].isdefault)[0]; //set mode to default layer
            if (!this.mapState.mode) {
                this.mapState.mode = Object.keys(this.appConfig.map.baseLayers)[0];
            }
        } else {
            this.mapState.mode = Object.keys(this.appConfig.map.baseLayers)[0];
        }

        if (this.appConfig.map.zoomLocations && this.appConfig.map.zoomLocations.length > 0) {
            this.appConfig.map.zoomLocations.unshift(
                {
                    "defaultName": (json.config.map.defaultName ? json.config.map.defaultName : "Default"),
                    "viewport": {
                        "zoom": json.config.map.viewport.zoom,
                        "center": {
                            "lat": json.config.map.viewport.center.lat,
                            "lng": json.config.map.viewport.center.lng
                        }
                    }
                }
            )
        }
    }

    getMaxZoom(mapTilerOverlays) {
        if (!mapTilerOverlays[mapTilerOverlays.length - 1].zoom) return 0;
        let max = mapTilerOverlays[mapTilerOverlays.length - 1].zoom[1];
        mapTilerOverlays.forEach((val, i) => {
            if (Object.keys(val).length !== 0) {
                if (val.zoom[1] > max) {
                    max = val.zoom[1];
                }
            }

        })
        return max;
    }

    getMinZoom(mapTilerOverlays) {
        if (!mapTilerOverlays[0].zoom) return 0;
        let min = mapTilerOverlays[0].zoom[0];
        mapTilerOverlays.forEach((val, i) => {
            if (Object.keys(val).length !== 0) {
                if (val.zoom[0] < min) {
                    min = val.zoom[0];
                }
            }

        })
        return min;
    }

    getMapZooms() {
        const {mapTilerOverlays, nonSpatial} = this.appConfig.map;
        if (nonSpatial && mapTilerOverlays && mapTilerOverlays.length > 0) {
            return {
                minZoom: this.getMinZoom(mapTilerOverlays),
                maxZoom: this.getMaxZoom(mapTilerOverlays)
            };
        } else {
            //Spatial with overlays
            if (mapTilerOverlays && mapTilerOverlays.length > 0) {
                return {
                    minZoom: 0,
                    maxZoom: Math.max(basemapMaxZoom, this.getMaxZoom(mapTilerOverlays))
                };
            } else {
                //Spatial with no overlays
                return {
                    minZoom: 0,
                    maxZoom: basemapMaxZoom
                };
            }
        }

    }

    getUrlParams() {
        if (document.location.search) {
            try {
                let search = document.location.search.substring(1);
                const urlParams = JSON.parse('{"' + search.replace(/&/g, '","').replace(/=/g, '":"') + '"}', function (key, value) {
                    return key === "" ? value : decodeURIComponent(value)
                });
                return urlParams;
            } catch (err) {
                console.log("err when getting urlParams");
                return "err when getting urlParams";
            }
        }
        return "";

    }

    environment = null;
    branchDisplayName = null;
    responseID = null;
    branchId = null;
    projectId = null;
    meta = null;

    @observable appConfig ={};

    @observable mapState = {
        mode: 'roadmap',
        element: null,
        editLayer: null
    };

    @observable pendingState = {
        element: null,
        popup: false,
        editing: false,
        savePopup: false,
        confirmationPopup: false,
    };

    @observable drawer = {
        active: null,
        open: true,
        expanded: false,
        disabled: false
    };

    @observable isMBIconClicked = false;

    @action toggleMBIconClick() {
        this.isMBIconClicked = !this.isMBIconClicked;
    }

    @observable addMoreButtons = false;

    @action setMoreButtons(setVal) {
        console.log('here..');
        this.addMoreButtons = setVal;
    }

    @observable mapDataSubmitted = false;

    @action setMapDataSubmitted(setVal) {
        this.mapDataSubmitted = setVal;
    }

    @observable surveyStarted = false;

    @action setSurveyStarted(started = true) {
        this.surveyStarted = started;
    }

    submitData(formData, cb) {
        console.log('inside submitData: ');
        if (this.mapDataSubmitted === true) {
            return;
        }


        let data = {
            responseId: this.responseID,
            branchId: this.branchId,//pull in directly from appStore rather than config(loadConfig should update this)
            projectId: this.projectId,
            urlParams: this.getUrlParams(),
            tableName: (this.environment === 'production') ? 'mycampus_elems' : "mycampus_elems_test",
            mapData: {
                elements: []
            },
            demographics: []
        };
        console.log('branchid BEFORE SUBMIT: ' + data.branchId);


        data.mapData.elements = formatElementsforExport(this.elementStore.elements);

        this.appConfig.survey.forEach(question => {
            data.demographics.push({
                uuid: question.uuid,
                type: question.type,
                text: question.text,
                answer: formData[question.uuid]
            });
        });

        console.log('data BEFORE SUBMIT: ' + JSON.stringify(data));

        let productionURL = 'https://23tkwzjvr2.execute-api.us-east-1.amazonaws.com/myc_postdata_s3_lambda/MYC-S3-Lambda';
        let stagingURL = 'https://dj3mppbaa4.execute-api.us-east-1.amazonaws.com/myc_postdata_s3_lambda_staging/MYC-S3-Lambda_STAGING';
        let responseURL = productionURL;
        if (this.isStaging) {
            responseURL = stagingURL;
        }
        //piping any non PROD responses to staging responses bucket
        postData(responseURL, JSON.stringify(data), (response) => {
            console.log(this.environment, response);
            if (response.ok) {
                this.setMapDataSubmitted(true);
                cb(true);

            } else {
                cb(false);
            }
        });
    }

    hasDemographics() {
        return !!(this.appConfig.survey && this.appConfig.survey.length > 0);
    }

    @action buildSurveySchema = () => {
        let surveyJSON = {
            "id": generateResponseID(),
            "clearInvisibleValues": "onHidden",
            "pages": [
                {
                    "name": "page1",
                    "elements": []
                }
            ]
        };

        let allAnswers = {};
        this.appConfig.survey.forEach(question => {
            question.answers.forEach(answer => {
                allAnswers[answer.uuid] = answer.text;
            })
        });

        this.appConfig.survey.forEach(question => {
            let questionJSON = {
                type: question.type,
                name: question.uuid,
                title: question.text
            };

            if (question.type === 'dropdown' || question.type === 'checkbox' || question.type === 'radiogroup') {
                questionJSON.choices = question.answers.map(answer => answer.text);
            }

            if (question.hasLogic) {
                questionJSON.visibleIf = "";
                question.logicAnswers.forEach((answer, i) => {
                    if (i > 0) {
                        questionJSON.visibleIf += ' or '
                    }
                    questionJSON.visibleIf += `{${question.logicQuestionUUID}} contains "${allAnswers[answer]}"`;
                });
            }

            surveyJSON.pages[0].elements.push(questionJSON)
        });

        //console.log("surveyJSON: " + JSON.stringify(surveyJSON));
        return surveyJSON;
    };

    @observable isMobile = false;
    @observable introModal = true;
    @observable standalone = false;

    @action setSavePopup(val) {
        this.pendingState.savePopup = val;
    }

    @action setConfirmationPopup(val) {
        this.pendingState.confirmationPopup = val;
    }

    @action setIntroModal(val) {
        this.introModal = val;
    }

    @action setPendingElementPosition(val) {
        this.pendingState.element.position = val;
    }

    @action setPendingElementReaction(val) {
        this.pendingState.element.reaction = val;
    }

    @action setPendingElementStarRating(val) {
        this.pendingState.element.starRating = val;
    }

    @action setPendingElementText(val) {
        this.pendingState.element.text = val;
    }

    @action setPendingStateEditing(val) {
        this.pendingState.editing = val;
    }

    @action setStandalone(standalone = true) {
        this.standalone = standalone;
    }

    @action setMapViewport(val) {
        this.appConfig.map.viewport = val;
    }

    @action toggleMapMode() {
        this.mapState.mode = Object.keys(this.appConfig.map.baseLayers).filter((key) => (key !== this.mapState.mode) && this.appConfig.map.baseLayers[key].enabled)[0]
    }

    @computed get inactiveMapMode() {
        return Object.keys(this.appConfig.map.baseLayers).filter((key) => (key !== this.mapState.mode) && this.appConfig.map.baseLayers[key].enabled)[0];
    }

    @computed get pendingInputRequired() {
        const icon = this.pendingState.element.icon;
        return icon.extra || icon.commentBox;
    }

    @action setIsMobile(val) {
        this.isMobile = val;
    }

    @action setDrawer(drawer) {
        this.tracking.click('Switched Drawers', {drawer: drawer.active});

        Object.keys(drawer).forEach((k) => {
            this.drawer[k] = drawer[k];
        });
    }

    @action disableDrawer(val) {
        this.drawer.disabled = val;
    }

    @computed get editMode() {
        return (this.pendingState.element !== null);
    }

    @computed get isDirty() {
        return (
            !this.mapDataSubmitted
            && (
                this.elementStore.elements.length > 0
                || this.surveyStarted
            )
        );
    }

    @computed get placingIconMode() {
        return this.editMode && !this.pendingState.savePopup;
    }

    @computed get thankYouLink() {
        let url = this.appConfig.content.thankyou.link;
        if (url && (url.indexOf('https://') < 0 && url.indexOf('http://') < 0)) {
            url = 'https://' + url;
        }
        try {
            let link = new URL(this.appConfig.content.thankyou.link);
            if (link.hostname && (link.hostname === 'www.surveygizmo.com' || link.hostname.indexOf('survey.alchemer.com') > -1)) {
                url += '?responseID=' + this.responseID
            }
        } catch (err) {
            console.log('ERR: thankYouLink ' + err.message);
        }
        return url;
    }

    @computed get allowSave() {
        if (this.pendingState.element) {
            if (this.pendingState.element.type === "icon") {
                return !!this.pendingState.element.position;
            } else if (this.pendingState.element.type === "polyline") {
                return !!this.pendingState.element.position[1];
            } else if (this.pendingState.element.type === "polygon") {
                return !!this.pendingState.element.position[2];
            }
        } else {
            return false;
        }
    }

    @computed get isStaging() {
        return !!(window.location.href && (window.location.href.indexOf('staging.dev.comap.sasaki.com') > -1 || window.location.href.indexOf('staging.comap.sasaki.com') > -1 || window.location.href.indexOf('dev.comap.sasaki.com') > -1 || window.location.href.indexOf('localhost') > -1));
    }

    @action configureElement(element) {
        this.mapState.element = element;
        if (this.mapState.element) {
            this.mapState.element.options.closePopupOnClick = false;
            this.mapState.element.options.viewport = this.appConfig.map.viewport;//CODE-REVIEW:RAJ:we shouldn't be setting for elements inidividually here, can this be centralized?
            this.mapState.editLayer = new L.LayerGroup();
            if (this.mapState.element.editTools) {
                this.mapState.element.editTools.editLayer = this.mapState.editLayer;
            }
            this.mapState.editLayer.addTo(this.mapState.element);
        }
    }

    @action clearPending = () => {
        this.pendingState.popup = false;
        this.pendingState.element = null;
        this.pendingState.editing = false;
        this.pendingState.savePopup = false;
        this.pendingState.confirmationPopup = false;
        this.drawer.disabled = false;
    };

    @action tryAutoSave() {
        if (!this.pendingInputRequired) {
            if (this.elementStore.inStore(this.pendingState.element)) {
                this.elementStore.updateElement(this.pendingState.element.uuid, this.pendingState.element);
            } else {
                this.elementStore.addElement(this.pendingState.element);
            }
            this.clearPending();
            return true;
        }
        return false;
    }

    @action returnLayerImage(url) {
        let layerURL = url;
        layerURL = layerURL.replace("{z}", `${this.appConfig.map.viewport.center.lng}`);
        layerURL = layerURL.replace("{x}", `${this.appConfig.map.viewport.center.lat}`);
        layerURL = layerURL.replace("{y}", `${this.appConfig.map.viewport.zoom}`);
        return layerURL;
    };

    //this is where defaults are added when any new breaking change are made
    applyDefaults(config) {
        if (!config.content.map) {
            config.content.map = {
                "bypass_header": "You are leaving the map, do you want to leave?",
                "bypass_content": "<p>You have not placed your comments on the map. We would love for you to have your say.</p><p>If you wish to add your comments and have your say on the map, please click 'Back to the Map.' If you wish to continue to the next questions, please click 'Continue.'</p>",
                "finish_button": "Done with the Map"
            }
        }
        return config;
    }
}



// WEBPACK FOOTER //
// ./src/stores/AppStore.js